import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, firstValueFrom, Subject, distinctUntilChanged, switchMap, from, catchError, EMPTY } from 'rxjs';
import { ButtonToggleProps } from 'src/app/components/button-toggle/button-toggle.component';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { CreateOrganizationDialogComponent } from 'src/app/dialogs/create-organization-dialog/create-organization-dialog.component';
import { OrganizationAccessDialogComponent, OrganizationAccessDialogData } from 'src/app/dialogs/organization-access-dialog/organization-access-dialog.component';
import { QueryParamManager, optionalString } from 'src/app/helpers/query-params.helper';
import { ClientType, Organization } from 'src/app/models/organization';
import { Paginator } from 'src/app/models/paginator';
import { Role, User } from 'src/app/models/user';
import { AclService } from 'src/app/services/acl.service';
import { AdminService } from 'src/app/services/admin.service';
import { MessageService } from 'src/app/services/message.service';

type OrganizationFilters = {
  query?: string;
  clientType?: ClientType;
}

@Component({
  selector: 'app-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss']
})
export class OrganizationsComponent implements OnInit {

  loading: boolean = false;
  done: boolean = false;
  /** The last load failed: the list says so instead of claiming there is no organisation. */
  failed: boolean = false;

  displayedColumns: string[] = ['name', 'headquartersAddress', 'createdAt', 'subscription', 'deletion', 'isSuspended', 'actions'];
  dataSource: MatTableDataSource<Organization> = new MatTableDataSource<Organization>([])

  /** Pending deletion scheduledFor per organization id, for the current page. */
  pendingDeletions: Map<string, string> = new Map();

  // Sequence guard: only the most recent batch may write pendingDeletions, so a
  // slow older reload can never overwrite the current page's badges.
  private pendingDeletionsRequest = 0;

  selectedClientType?: ClientType;

  public role = Role;

  /** Entering a client organisation (organisation access): platform Super Admins and the Owner, outside an access. */
  readonly canEnter: boolean;

  protected readonly manager = new QueryParamManager<OrganizationFilters, Organization>(
    {
      defaultLimit: 10,
      defaultSort: 'name',
      fields: {
        query: {
          paramKey: 'query',
          deserialize: optionalString,
          serialize: (v) => v || undefined,
        },
        clientType: {
          paramKey: 'clientType',
          deserialize: (v) => (typeof v === 'string' ? (v as ClientType) : undefined),
          serialize: (v) => v || undefined,
        },
      },
    },
    this.router,
  );

  // One button per client type. All organizations are shown when none is selected.
  public readonly clientTypeButtons: (ButtonToggleProps & {
    value?: ClientType
  })[] = [
    { label: 'ADMIN.TABS.ALL', selected: true, value: undefined },
    { label: 'ADMIN.TABS.DEMO', value: ClientType.DEMO },
    { label: 'ADMIN.TABS.PROSPECT', value: ClientType.PROSPECT },
    { label: 'ADMIN.TABS.CLIENT', value: ClientType.CLIENT },
  ];

  private readonly reload$ = new Subject<void>();

  constructor(
    private readonly _message: MessageService,
    private readonly aclService: AclService,
    private readonly adminService: AdminService,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef
  ) {
    this.canEnter = this.aclService.isSuperAdmin() && !this.aclService.isInAccess();
    if (this.canEnter) {
      this.displayedColumns.splice(this.displayedColumns.indexOf('actions'), 0, 'access');
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.done = false;

    this.reload$
      .pipe(
        debounceTime(200),
        switchMap(() => {
          this.applyQueryParams();
          this._message.emitChange('LOADING', 'START');
          this.loading = true;
          this.failed = false;
          return from(this.adminService.retrieveOrganizations(
            this.manager.pager.page,
            this.manager.pager.limit,
            this.manager.pager.offset,
            this.manager.pager.sort,
            this.manager.filters.query,
            this.manager.filters.clientType,
          )).pipe(catchError((err) => {
            this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
            this._message.emitChange('LOADING', 'END');
            this.done = true;
            this.loading = false;
            this.failed = true;
            return EMPTY;
          }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        Object.assign(this.manager.pager, result);
        this.dataSource = new MatTableDataSource<Organization>(this.manager.pager.docs);
        this._message.emitChange('LOADING', 'END');
        this.loading = false;
        this.done = true;
        this.refreshPendingDeletions();
      });

    let isFirstLoad = true;
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.manager.fromParams(params);

        // Reflect the URL clientType (or default to All) on the toggle.
        this.selectedClientType = this.manager.filters.clientType;
        const active = this.clientTypeButtons.find((b) => b.value === this.manager.filters.clientType);
        this.clientTypeButtons.forEach((b) => (b.selected = b === active || (!active && b.value === undefined)));

        if (isFirstLoad) {
          isFirstLoad = false;
          this.reload$.next(); // triggers the switchMap pipeline, which sets loading/done when it resolves
        }
      });
  }

  async retrieveOrganizations() {
    try {
      this._message.emitChange('LOADING', 'START');
      this.loading = true;
      this.failed = false;
      const pager = this.manager.pager;

      const result = await this.adminService.retrieveOrganizations(
        pager.page,
        pager.limit,
        pager.offset,
        pager.sort,
        this.manager.filters.query,
        this.manager.filters.clientType,
      );

      Object.assign(pager, result);
      this.dataSource = new MatTableDataSource<Organization>(pager.docs);
    } catch (err) {
      this.failed = true;
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    } finally {
      this._message.emitChange('LOADING', 'END');
      this.loading = false;
    }
  }

  async suspendOrganisation(event: any, organization: Organization) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: organization.isSuspended ? 'ADMIN.RESTORE_ORGANIZATION' : 'ADMIN.SUSPEND_ORGANIZATION',
        text: this.translate.instant(organization.isSuspended ? 'ADMIN.RESTORE_ORGANIZATION_TEXT' : 'ADMIN.SUSPEND_ORGANIZATION_TEXT', organization),
        button: {
          text: organization.isSuspended ? 'ACTIONS.RESTORE' : 'ACTIONS.SUSPEND',
          class: organization.isSuspended ? 'main-button' : 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed) {
      try {
        await this.adminService.suspendOrganization(organization._id ?? "", !organization.isSuspended);
        const pager = this.manager.pager;
        const orgIndex = pager.docs.findIndex((o: Organization) => o._id === organization._id);
        if (orgIndex !== -1) {
          pager.docs[orgIndex].isSuspended = !organization.isSuspended;
          event.source.checked = !organization.isSuspended;
          this.toastr.info(this.translate.instant(organization.isSuspended ? 'ADMIN.SUSPENDED_ORGANIZATION' : 'ADMIN.RESTORED_ORGANIZATION', organization))
        }
      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    } else {

      const pager = this.manager.pager;
      const orgIndex = pager.docs.findIndex((o: Organization) => o._id === organization._id);
      if (orgIndex !== -1) {
        pager.docs[orgIndex].isSuspended = organization.isSuspended;
        event.source.checked = !organization.isSuspended;
      }
    }

  }

  /**
   * Refresh pending-deletion badges for the current page with ONE batched request.
   * A probe failure (network/5xx) is NOT conflated with "no pending deletion": on
   * failure we keep the previous badges rather than hiding them.
   */
  private async refreshPendingDeletions() {
    const ids = this.manager.pager.docs.map((o: Organization) => o._id).filter((id): id is string => !!id);
    if (ids.length === 0) {
      this.pendingDeletions = new Map();
      return;
    }
    const requestId = ++this.pendingDeletionsRequest;
    try {
      const pending = await this.adminService.retrievePendingDeletions(ids);
      if (requestId !== this.pendingDeletionsRequest) {
        return; // a newer reload superseded this batch
      }
      this.pendingDeletions = new Map(
        (pending ?? []).map((p) => [p.organizationId ?? '', p.scheduledFor]),
      );
    } catch {
      // Keep the previous badges; the Delete button stays gated by the backend
      // idempotency guard (409), so a stale UI cannot double-schedule.
      if (requestId !== this.pendingDeletionsRequest) {
        return;
      }
    }
  }

  async cancelDeletion(organization: Organization) {
    if (!organization._id) return;
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'ORGANIZATION.CANCEL_DELETE_TITLE',
        text: this.translate.instant('ORGANIZATION.CANCEL_DELETE_TEXT', { name: organization.name }),
        button: {
          text: 'ACTIONS.CANCEL',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed) {
      try {
        await this.adminService.cancelOrganizationDeletion(organization._id);
        this.pendingDeletions.delete(organization._id);
        this.toastr.success(this.translate.instant('ORGANIZATION.CANCEL_DELETE_SUCCESS'));
      } catch {
        this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
      }
    }
  }

  onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.manager.onSortChange(event);
      this.reload$.next();
    }
  }

  onQueryChange(query: any) {
    if (typeof query === 'string') {
      this.manager.resetPage();
      this.manager.filters.query = query;
      this.reload$.next();
    }
  }

  handlePage(event: PageEvent) {
    this.manager.handlePage(event);
    this.reload$.next();
  }

  onClientTypeChange(index: number) {
    const clientType = this.clientTypeButtons[index]?.value;
    this.selectedClientType = clientType;
    this.manager.resetPage();
    this.manager.filters.clientType = clientType;
    this.reload$.next();
  }

  private applyQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.manager.toParams(),
    });
  }

  formatDate(date: Moment | Date | string | undefined) {
    return date ? moment(date).format('ll') : '';
  }

  /** Why this organisation cannot be entered right now, as a translation key; undefined when it can. */
  enterBlockedReason(organization: Organization): string | undefined {
    if (organization.isSuspended) {
      return 'ACCESS.BLOCKED_SUSPENDED';
    }
    if (organization._id && this.pendingDeletions.has(organization._id)) {
      return 'ACCESS.BLOCKED_DELETION';
    }
    return undefined;
  }

  enter(organization: Organization) {
    if (!organization._id || this.enterBlockedReason(organization)) return;
    const data: OrganizationAccessDialogData = { organization: { id: organization._id, name: organization.name } };
    this.dialog.open(OrganizationAccessDialogComponent, {
      panelClass: 'dialog-container',
      width: '520px',
      data
    });
  }

  async createOrganization() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {}
    }
    const dialog = this.dialog.open(CreateOrganizationDialogComponent, config);
    // The dialog sends the request itself and only closes once the organisation exists.
    const result: { created: boolean, organization?: Organization, user?: User } = await firstValueFrom(dialog.afterClosed());
    if (result && result.created && result.organization) {
      this.toastr.success(result.user
        ? this.translate.instant('ADMIN.ORGANIZATION_CREATED', { name: result.organization.name, email: result.user.email })
        : this.translate.instant('ADMIN.ORGANIZATION_CREATED_WITHOUT_ADMIN', { name: result.organization.name }));
      await this.retrieveOrganizations();
    }
  }
}
